import { Grid, IconButton, CircularProgress } from "@mui/material";
import Iconify from "../../../components/minimal/iconify";
import { Steps } from "../../../components/Steps";
import { m } from "framer-motion";
import FormProvider from "../../../components/minimal/hook-form";
import { useLocales } from "../../../locales";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { useEventContext } from "../../../context-state/EventContext";
import { useEffect, useRef, useState } from "react";
import { PayForm } from "./PayForm";
import { DetailsForm } from "./DetailsForm";
import { generateInitialOrderDetailsState, getTotalTicketsNumber } from "../../../utils/eventPage";
import { buildUserEventSchemaByEventInputData, eventInputDataToFields } from "../../../utils/transformers";
import { getOrderPrice } from "../../../api/index.js";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSettingsContext } from "../../../components/minimal/settings/SettingsContext";
import DynamicError from "../../../components/DynamicError";
import { useSearchParams } from "react-router-dom";
import { Card, Typography, Link } from "@mui/material";
import checkoutStore from "../../../stores/checkout.store";
import useResponsive from "../../../hooks/minimal/useResponsive";
import { isInIframe } from "../../../utils/iframe";

export const OrderDetails = ({ isLoading, setActiveStep, activeStep, chosenTickets, createOrder, paymentFormError, forceDisableBtn }) => {

    const { eventPageContext } = useEventContext()
    const { presetsColor } = useSettingsContext()
    const { tickets, coupon, event, account, initialUserEventDetails, seasonTicket, appliedHrcQS, appliedCouponQs } = eventPageContext
    const formFields = eventInputDataToFields(event.inputData)

    const [isLoadingPrice, setIsLoadingPrice] = useState(false)
    const [totalToPayAfterCoupon, setTotalToPayAfterCoupon] = useState(0)
    const [pricePerTicket, setPricePerTicket] = useState({})
    const [totalToPayToSendPayMe, setTotalToPayToSendPayMe] = useState(0)
    const [nimiFee, setNimiFee] = useState(0)
    const [priceError, setPriceError] = useState("")
    const oneBuyerDetails = event.isSeatsEvent || event.isOneUserFormPerOrder
    const { initialState, summary, totalToPayLocal, nimiFeeLocal } = generateInitialOrderDetailsState(tickets, chosenTickets, oneBuyerDetails, formFields.custom_questions, initialUserEventDetails, pricePerTicket)
    const totalAmountOfTickets = getTotalTicketsNumber(chosenTickets)
    const [searchParams, setSearchParams] = useSearchParams();

    const isDesktop = useResponsive('up', 'md');


    const { translate } = useLocales()
    const stepsRef = useRef(null)
    const STEPS = [translate('event_page.chosen_ticket'), translate('event_page.enter_details'), translate('event_page.payment')];

    const { colors = {} } = event.design
    let theme = createTheme({})

    theme = createTheme({
        palette: {
            primary: theme.palette.augmentColor({
                color: {
                    main: colors.progressBar || presetsColor.main,
                },
                name: 'primary',
            })
        },
    });

    const getPrice = async () => {
        try {
            setIsLoadingPrice(true)
            let endUserSeasonTicketId = seasonTicket?.endUserSeasonTicket?._id

            const ticketsIdsChosen = []
            const ticketsIds = Object.keys(chosenTickets)
            for (const ticketId of ticketsIds) {
                const amountOfOrdersPerTickets = chosenTickets[ticketId].amount
                if (amountOfOrdersPerTickets) {
                    for (let i = 0; i < amountOfOrdersPerTickets; i++) {
                        ticketsIdsChosen.push(ticketId)
                    }
                }
            }

            const orderData = { couponId: coupon?._id, event, ticketsIdsChosen, endUserSeasonTicketId }
            const { data } = await getOrderPrice({ accountId: account._id, orderData })

            setTotalToPayToSendPayMe(data.finalPriceWithNimiFeeForPaymentProvider)
            setNimiFee(data.nimiFee)
            // Extract discountedPricePerTicket from data with a default empty array
            const { discountedPricePerTicket = [] } = data
            // turn the discountedPricePerTicket array into a map based on the _id prop
            setPricePerTicket(discountedPricePerTicket)
            setTotalToPayAfterCoupon(data.finalPriceWithNimiFee)

        } catch (e) {
            console.log(e);
            setPriceError(translate('dashboard.events.refresh_page_error'))
        } finally {
            setIsLoadingPrice(false)
        }
    }

    // This validation is not working out of the box! since it's an array of N amount of tickets i did the validation manually, this is only a schema
    const ticketSchema = Yup.object().shape(buildUserEventSchemaByEventInputData(formFields));

    const formSchema = Yup.object().shape({
        items: Yup
            .array()
            .of(Yup.object().shape(ticketSchema))
            .required("Must have fields")
            .min(1, "Minimum of 1 field")
    });

    const defaultValues = { couponCode: '', items: initialState }

    const methods = useForm({ defaultValues, validationSchema: formSchema, mode: "onChange" });

    const { watch, reset } = methods;

    const onValidFormSubmit = () => {
        if (totalToPayAfterCoupon === 0 || checkoutStore.splitPayment) {
            createOrder({ userEvents: watch().items, totalToPay: totalToPayAfterCoupon, coupon, summary })
            return
        }

        setActiveStep(2)
    }

    // check if we have unapplied query strings
    const unAppliedQueryStrings = () => {
        const unappliedCoupon = searchParams.get('coupon') && !appliedCouponQs
        const hrcCode = searchParams.get('hrc') || localStorage.getItem('hrcCode')
        const unappliedHrc = hrcCode && !appliedHrcQS
        if (unappliedCoupon || unappliedHrc) {
            return true
        }
    }

    // Scroll stepper to top view when step change
    useEffect(() => {
        if (activeStep === 1) {
            if (stepsRef.current) {
                stepsRef.current.scrollIntoView({ behavior: 'smooth' })
            }
            if (unAppliedQueryStrings()) {
                return
            }
            getPrice()
        }
    }, [
        activeStep,
        seasonTicket,
        coupon,
        appliedCouponQs,
        appliedHrcQS
    ]);

    useEffect(() => {
        if (seasonTicket?.endUser) {
            const { initialState } = generateInitialOrderDetailsState(tickets, chosenTickets, oneBuyerDetails, formFields.custom_questions, seasonTicket.endUser, pricePerTicket)
            reset({ couponCode: '', items: initialState })
        }
    }, [seasonTicket]);

    if (activeStep === 6) {
        return (<DynamicError
            title={translate('event_page.payme_error.title')}
            subtitle={translate('event_page.payme_error.subtitle')}
            actionBtnMsg={translate('event_page.payme_error.action_btn_msg')}
            onActionBtnClick={() => setActiveStep(2)} />)
    }


    return (
        <FormProvider methods={methods}>
            <Grid container spacing={2} justifyContent="center" sx={{ position: "relative" }}>
                <IconButton
                    onClick={() => { setActiveStep(activeStep - 1) }}
                    sx={{ width: 40, position: "absolute", height: 40, left: 10, top: 10, zIndex: 10 }}
                >
                    <Iconify icon="eva:arrow-forward-outline" />
                </IconButton>
                <Grid item xs={12} md={8} ref={stepsRef}>
                    {isInIframe() ?
                        <Steps initial={{ opacity: 0, y: -80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} component={m.div} activeStep={activeStep} steps={STEPS} />
                        :
                        <ThemeProvider theme={theme}>
                            <Steps initial={{ opacity: 0, y: -80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} component={m.div} activeStep={activeStep} steps={STEPS} />
                        </ThemeProvider>
                    }
                </Grid>
                {
                    activeStep === 1 ?
                        <DetailsForm
                            formFields={formFields}
                            totalAmountOfTickets={totalAmountOfTickets}
                            onValidFormSubmit={onValidFormSubmit}
                            summary={summary}
                            isLoading={isLoading || isLoadingPrice}
                            totalToPayAfterCoupon={totalToPayAfterCoupon}
                            pricePerTicket={pricePerTicket}
                            nimiFee={nimiFee}
                            isLoadingPrice={isLoadingPrice}
                            priceError={priceError}
                        />
                        : null
                }
                {
                    activeStep === 2 ? (
                        checkoutStore.splitPayment ? (
                            <Grid initial={{ opacity: 0, x: -80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} component={m.div} item xs={12} md={12}>
                                <Card sx={{ p: 2, mb: 2 }}>
                                    {checkoutStore.splitPaymentIframeLink ? (
                                        <iframe
                                            src={checkoutStore.splitPaymentIframeLink}
                                            frameBorder="0"
                                            style={{ width: '100%', height: '500px' }}
                                            title="Split Payment"
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                            <CircularProgress />
                                        </div>
                                    )}
                                </Card>
                            </Grid>
                        ) : (
                            <PayForm
                                onSubmit={() => createOrder({ userEvents: watch().items, totalToPay: totalToPayAfterCoupon, coupon, summary, totalToPayToSendPayMe })}
                                totalToPayAfterCoupon={totalToPayAfterCoupon}
                                isLoading={isLoading}
                                forceDisableBtn={forceDisableBtn}
                                paymentFormError={paymentFormError}
                            />
                        )
                    ) : null
                }
            </Grid>
        </FormProvider>
    )
}
